/**
 * CookieBanner.jsx — Banner inicial + modal de configuración de cookies.
 *
 * Mostrado al pie de la página en la primera visita.
 * Permite Aceptar todas / Rechazar todas / Configurar.
 *
 * Conforme a la Guía AEPD Nov 2023:
 *   • Los botones "Aceptar" y "Rechazar" tienen el mismo nivel de visibilidad.
 *   • Por defecto NO se cargan cookies no necesarias hasta consentimiento.
 *   • El usuario puede modificar sus preferencias en cualquier momento.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings, X, ShieldCheck, BarChart3, Megaphone } from 'lucide-react';
import { useCookieConsent } from '@/context/CookieConsentContext';

// ─── Categorías para el panel ───────────────────────────────────────────────
const CATEGORIES = [
  {
    key: 'necessary',
    icon: ShieldCheck,
    title: 'Cookies estrictamente necesarias',
    locked: true,
    description: 'Imprescindibles para que la web funcione. Permiten iniciar sesión, mantener tu sesión activa y recordar tus preferencias de cookies. No se pueden desactivar.',
    cookies: [
      { name: 'revix_cookie_consent_v1', dur: '12 meses', purpose: 'Guardar tu elección sobre cookies.' },
      { name: 'session_id', dur: 'Sesión', purpose: 'Mantener tu sesión activa al navegar.' },
    ],
  },
  {
    key: 'analytics',
    icon: BarChart3,
    title: 'Cookies analíticas',
    description: 'Nos ayudan a entender cómo se usa la web (páginas más visitadas, errores, tiempos de carga) para mejorarla. Datos anónimos y agregados, IP anonimizada.',
    cookies: [
      { name: '_ga', dur: '13 meses', provider: 'Google Analytics 4', purpose: 'Distinguir usuarios anónimos.' },
      { name: '_ga_<ID>', dur: '13 meses', provider: 'Google Analytics 4', purpose: 'Persistir el estado de la sesión.' },
    ],
  },
  {
    key: 'marketing',
    icon: Megaphone,
    title: 'Cookies de marketing',
    description: 'Se usan para mostrarte anuncios relevantes en otras webs si lo activamos en el futuro (Google Ads, remarketing). Sin tu permiso no se carga ningún píxel ni anuncio.',
    cookies: [
      { name: '(no activas actualmente)', dur: '—', purpose: 'Reservado para futuras campañas. Si las activamos, te pediremos tu consentimiento.' },
    ],
  },
];

// ─── Banner inicial ─────────────────────────────────────────────────────────
function Banner() {
  const { showBanner, acceptAll, rejectAll, openSettings } = useCookieConsent();

  if (!showBanner) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Aviso sobre el uso de cookies"
      className="fixed inset-x-0 bottom-0 z-[100] bg-white text-[#111] border-t border-[#E5E5EA] shadow-2xl"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="hidden sm:flex w-10 h-10 rounded-full bg-amber-100 items-center justify-center shrink-0">
              <Cookie className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-sm leading-relaxed">
              <p className="font-semibold text-[#111] mb-1">Tu privacidad nos importa</p>
              <p className="text-[#555]">
                Usamos cookies estrictamente necesarias para que la web funcione. Si lo aceptas,
                también usaremos cookies analíticas (Google Analytics) para mejorar la experiencia.
                Puedes <button
                  type="button"
                  onClick={openSettings}
                  className="underline underline-offset-2 hover:text-[#111] font-medium"
                  data-testid="cookie-banner-link-config"
                >configurar tus preferencias</button> o consultar nuestra{' '}
                <Link to="/cookies" className="underline underline-offset-2 hover:text-[#111] font-medium">Política de cookies</Link>.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              type="button"
              onClick={rejectAll}
              data-testid="cookie-banner-reject"
              className="px-4 py-2.5 rounded-full text-sm font-medium border border-[#D1D1D6] hover:bg-[#F2F2F7] transition-colors"
            >
              Rechazar todas
            </button>
            <button
              type="button"
              onClick={openSettings}
              data-testid="cookie-banner-config"
              className="px-4 py-2.5 rounded-full text-sm font-medium border border-[#D1D1D6] hover:bg-[#F2F2F7] transition-colors flex items-center justify-center gap-1.5"
            >
              <Settings className="w-3.5 h-3.5" />
              Configurar
            </button>
            <button
              type="button"
              onClick={acceptAll}
              data-testid="cookie-banner-accept"
              className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#111] text-white hover:bg-[#000] transition-colors"
            >
              Aceptar todas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modal de configuración ─────────────────────────────────────────────────
function SettingsModal() {
  const {
    showSettings, consent, closeSettings,
    updatePreferences, acceptAll, rejectAll,
  } = useCookieConsent();
  const [draft, setDraft] = useState({ analytics: false, marketing: false });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (showSettings) {
      setDraft({
        analytics: consent.analytics || false,
        marketing: consent.marketing || false,
      });
    }
  }, [showSettings, consent]);

  if (!showSettings) return null;

  const toggle = (key) => setDraft((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Configuración de cookies"
      data-testid="cookie-settings-modal"
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
      onClick={closeSettings}
    >
      <div
        className="w-full max-w-2xl max-h-[92vh] bg-white text-[#111] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[#E5E5EA] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-semibold text-base">Configuración de cookies</h2>
              <p className="text-xs text-[#6E6E73]">Decide qué cookies aceptas en Revix.es</p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeSettings}
            className="text-[#6E6E73] hover:text-[#111] p-1"
            data-testid="cookie-settings-close"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-4 flex-1">
          <p className="text-sm text-[#555] leading-relaxed">
            Las cookies estrictamente necesarias siempre están activas. Para las demás necesitamos
            tu permiso explícito. Puedes cambiar de opinión en cualquier momento desde el enlace
            "Cookies" del pie de página.
          </p>

          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isLocked = cat.locked;
            const isOn = isLocked ? true : draft[cat.key];
            const isExpanded = expanded === cat.key;

            return (
              <div
                key={cat.key}
                className="rounded-xl border border-[#E5E5EA] bg-white"
                data-testid={`cookie-category-${cat.key}`}
              >
                <div className="flex items-start gap-3 p-4">
                  <Icon className="w-5 h-5 text-[#555] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-medium text-sm">{cat.title}</h3>
                      {isLocked ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F2F2F7] text-[#6E6E73] font-medium uppercase tracking-wide">
                          Siempre activas
                        </span>
                      ) : (
                        <button
                          type="button"
                          role="switch"
                          aria-checked={isOn}
                          onClick={() => toggle(cat.key)}
                          data-testid={`cookie-toggle-${cat.key}`}
                          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                            isOn ? 'bg-[#111]' : 'bg-[#D1D1D6]'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
                              isOn ? 'left-[calc(100%-22px)]' : 'left-0.5'
                            }`}
                          />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-[#555] mt-1.5 leading-relaxed">{cat.description}</p>
                    <button
                      type="button"
                      onClick={() => setExpanded(isExpanded ? null : cat.key)}
                      className="text-xs text-[#0066CC] hover:underline mt-2"
                      data-testid={`cookie-detail-toggle-${cat.key}`}
                    >
                      {isExpanded ? 'Ocultar detalles' : 'Ver cookies'}
                    </button>
                    {isExpanded && (
                      <div className="mt-3 rounded-lg bg-[#F8F8F9] border border-[#E5E5EA] overflow-hidden">
                        <table className="w-full text-[11px]">
                          <thead className="bg-[#EFEFF4] text-[#6E6E73]">
                            <tr>
                              <th className="text-left px-3 py-1.5 font-medium">Cookie</th>
                              <th className="text-left px-3 py-1.5 font-medium">Duración</th>
                              <th className="text-left px-3 py-1.5 font-medium">Finalidad</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cat.cookies.map((c, i) => (
                              <tr key={i} className="border-t border-[#E5E5EA]">
                                <td className="px-3 py-1.5 font-mono">{c.name}</td>
                                <td className="px-3 py-1.5 text-[#6E6E73]">{c.dur}</td>
                                <td className="px-3 py-1.5 text-[#555]">
                                  {c.provider ? `${c.provider} · ` : ''}{c.purpose}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <p className="text-xs text-[#6E6E73] leading-relaxed pt-2">
            Más información en nuestra <Link to="/cookies" className="underline">Política de cookies</Link>,{' '}
            <Link to="/privacidad" className="underline">Política de privacidad</Link> y{' '}
            <Link to="/aviso-legal" className="underline">Aviso legal</Link>.
          </p>
        </div>

        {/* Footer */}
        <div className="border-t border-[#E5E5EA] p-4 flex flex-col sm:flex-row gap-2 sm:justify-end shrink-0 bg-[#FAFAFA]">
          <button
            type="button"
            onClick={rejectAll}
            data-testid="cookie-settings-reject"
            className="px-4 py-2.5 rounded-full text-sm font-medium border border-[#D1D1D6] hover:bg-white transition-colors"
          >
            Rechazar todas
          </button>
          <button
            type="button"
            onClick={() => updatePreferences(draft)}
            data-testid="cookie-settings-save"
            className="px-4 py-2.5 rounded-full text-sm font-medium border border-[#D1D1D6] hover:bg-white transition-colors"
          >
            Guardar selección
          </button>
          <button
            type="button"
            onClick={acceptAll}
            data-testid="cookie-settings-accept"
            className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#111] text-white hover:bg-[#000] transition-colors"
          >
            Aceptar todas
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Wrapper exportado ──────────────────────────────────────────────────────
export default function CookieBannerAndSettings() {
  return (
    <>
      <Banner />
      <SettingsModal />
    </>
  );
}
